import type { ReactNode } from 'react';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { links } from '@eis/core';
import { RequirePermission } from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import {
  partnerLabel,
  partnersByRole,
  roleNameOf,
  type PartnerRoleRule,
  type ResolvedPartner,
} from '../model/partners';

/**
 * A partner, as the directory could name it: the name when known, the id marked UNRESOLVED when
 * not (SSOT §4.2 — the same rule the Group Policy list applies). A blank would claim the partner
 * has no name; the id says nobody here knows it. The type flag says whether the tariff's type
 * constraint was actually checked for this partner (SDD-011 P-10).
 *
 * Since SDD-014 the name is the door to the Partner area for whoever may enter it (`partners.view`,
 * through the typed link builder — this module never imports that one), and the type the
 * directory answered shows as a chip: the quick info a reader wants before opening the partner.
 */
export function PartnerName({ partner }: { readonly partner: ResolvedPartner }): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const label = partnerLabel(partner);
  const code = partner.link?.partnerId ?? '';
  const text = label.text === '' ? '—' : label.text;
  const href = links.partners.partner(code);
  return (
    <Stack
      direction="row"
      spacing={0.75}
      useFlexGap
      sx={{ alignItems: 'center', flexWrap: 'wrap' }}
    >
      {code === '' ? (
        <span>{text}</span>
      ) : (
        <RequirePermission anyOf={[p.partners.view]} fallback={<span>{text}</span>}>
          <Link
            href={href}
            data-testid="partner-link"
            onClick={(event) => {
              event.preventDefault();
              void navigate({ href });
            }}
          >
            {text}
          </Link>
        </RequirePermission>
      )}
      {label.typeChecked && partner.partnerTypeCode !== undefined && partner.partnerTypeCode !== '' && (
        <Chip
          size="small"
          variant="outlined"
          data-testid="partner-type"
          label={t(`policies.partners.types.${partner.partnerTypeCode}` as never, {
            defaultValue: partner.partnerTypeCode,
          })}
        />
      )}
      {!label.resolved && label.text !== '' && (
        <Tooltip title={t('policies.partners.unresolvedHint')}>
          <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
            <HelpOutlineIcon fontSize="inherit" color="action" />
            <Typography variant="caption" color="text.secondary">
              {t('policies.partners.unresolved')}
            </Typography>
          </Stack>
        </Tooltip>
      )}
      {!label.typeChecked && (
        <Tooltip title={t('policies.partners.typeUncheckedHint')}>
          <Chip size="small" variant="outlined" label={t('policies.partners.typeUnchecked')} />
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * A segment's partners, per role. The role's display name comes from the pinned tarif's rules
 * when the screen has them; the tag code otherwise — never a name invented here.
 */
export function PartnerList({
  partners,
  rules,
}: {
  readonly partners: readonly ResolvedPartner[] | undefined;
  readonly rules: readonly PartnerRoleRule[] | undefined;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const groups = partnersByRole(partners);
  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('policies.partners.none')}
      </Typography>
    );
  }
  return (
    <Stack component="dl" spacing={1} sx={{ m: 0 }} data-testid="partner-list">
      {groups.map((group) => (
        <Stack key={group.roleErn} spacing={0.25}>
          <Typography component="dt" variant="caption" color="text.secondary">
            {roleNameOf(group.roleErn, rules)}
          </Typography>
          {group.partners.map((partner, index) => (
            <Typography
              component="dd"
              variant="body2"
              key={`${partner.link?.partnerId ?? ''}-${String(index)}`}
              sx={{ m: 0 }}
            >
              <PartnerName partner={partner} />
            </Typography>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
